import { z } from 'zod'
import { Slug } from './ids'
import { PLATFORMS, PLATFORM_KEYS } from './platforms'

export const SECTION_IDS = ['brands', 'locations', 'specials', 'contacts', 'audience', 'social', 'assets', 'links', 'faq'] as const
export const SectionId = z.enum(SECTION_IDS)
export type SectionId = z.infer<typeof SectionId>

export const ClientStatus = z.enum(['draft', 'invited', 'in_progress', 'submitted', 'archived'])
export type ClientStatus = z.infer<typeof ClientStatus>

export const MemberRole = z.enum(['owner', 'collaborator'])
export type MemberRole = z.infer<typeof MemberRole>

/**
 * What a member role means inside the portal. It is portal access only, never the person's job at
 * the client; that goes in `title` ("Director of Marketing"). Shown wherever a role is picked or listed.
 * The stored value `owner` is labelled "Manager" everywhere people see it: the first person we invite
 * is simply the one who can manage who else gets in, whatever their job at the company.
 */
export const MEMBER_ROLES: Record<MemberRole, { label: string; hint: string }> = {
  owner: { label: 'Manager', hint: 'Can fill in every section, invite and remove teammates, and submit the onboarding.' },
  collaborator: { label: 'Collaborator', hint: 'Can fill in and edit every section. Cannot change who has access or submit.' },
}

export const PlatformSetting = z.object({ include: z.boolean(), managed: z.boolean() })
export type PlatformSetting = z.infer<typeof PlatformSetting>

/** Optional image or looping video behind the welcome hero, with a dark overlay so the copy stays readable. */
export const WelcomeMedia = z.object({
  url: z.string().url(),
  storagePath: z.string(),
  type: z.enum(['image', 'video']),
  /** 0 = media as is, 1 = fully dark. Default 0.6. */
  overlay: z.number().min(0).max(1).default(0.6),
})
export type WelcomeMedia = z.infer<typeof WelcomeMedia>

type SectionEntry = { id: SectionId; enabled: boolean }

/**
 * Configs saved before a step existed don't list it. Slot each missing step in after the step that
 * precedes it in SECTION_IDS (Contacts lands right after Locations & hours), switched on. Steps that
 * have since been retired (Additional material) are dropped.
 */
export function withAllSections(sections: SectionEntry[]): SectionEntry[] {
  const out = [...sections]
  SECTION_IDS.forEach((id, i) => {
    if (out.some((s) => s.id === id)) return
    const before = SECTION_IDS.slice(0, i).reverse().find((p) => out.some((s) => s.id === p))
    const at = before ? out.findIndex((s) => s.id === before) + 1 : 0
    out.splice(at, 0, { id, enabled: true })
  })
  return out
}

/** The staff composer's output: what this client's portal contains and who appears on it. */
export const PortalConfig = z.object({
  sections: z
    .array(z.object({ id: z.string(), enabled: z.boolean() }))
    .transform((s) => withAllSections(s.filter((x): x is SectionEntry => SectionId.safeParse(x.id).success))),
  platforms: z.record(z.enum(PLATFORM_KEYS), PlatformSetting),
  /** Per-brand overrides, e.g. one concept has no TikTok. */
  platformOverrides: z.record(Slug, z.partialRecord(z.enum(PLATFORM_KEYS), PlatformSetting.partial())).default({}),
  team: z.array(z.object({ staffId: Slug, roleLabel: z.string(), order: z.number().int() })),
  modules: z.object({ galleries: z.boolean() }),
  scopeNotes: z.string().default(''),
  welcomeMedia: WelcomeMedia.nullable().default(null),
  /** Per-client overrides for fixed copy. Blank means the default in packages/shared/src/copy.ts. */
  copy: z
    .object({
      welcomeHeadline: z.string().optional(),
      welcomeBody: z.string().optional(),
      teamTitle: z.string().optional(),
      teamBody: z.string().optional(),
    })
    .default({}),
  /** Subset of faqQuestions to show; undefined = every active question. */
  faqQuestionIds: z.array(z.string()).optional(),
  templateId: z.string().default('restaurant-default'),
})
export type PortalConfig = z.infer<typeof PortalConfig>

/**
 * done: required fields the client has confirmed (or wrote themselves). filled: required fields that
 * have any value at all, confirmed or not. The client's rail shows done; the console shows filled.
 */
export const ProgressSummary = z.object({
  overall: z.object({ done: z.number(), total: z.number(), filled: z.number().optional() }),
  sections: z.record(z.string(), z.object({ done: z.number(), total: z.number(), unconfirmed: z.number(), filled: z.number().optional() })),
})
export type ProgressSummary = z.infer<typeof ProgressSummary>

export const ClientDoc = z.object({
  name: z.string().min(1),
  slug: Slug,
  status: ClientStatus,
  createdAt: z.number(),
  createdBy: z.string(),
  invitedAt: z.number().nullable().default(null),
  submittedAt: z.number().nullable().default(null),
  submittedBy: z.string().nullable().default(null),
  lastActivityAt: z.number(),
  progress: ProgressSummary.optional(),
  /** The built-in test client admins sign in as (TEST_ACCOUNTS). Tagged in the console, never a real client. */
  test: z.boolean().optional(),
})
export type ClientDoc = z.infer<typeof ClientDoc>

export const MemberDoc = z.object({
  email: z.string().email(),
  name: z.string().default(''),
  /** Their job at the client, e.g. "Director of Marketing". Free text, optional. */
  title: z.string().default(''),
  role: MemberRole,
  invitedBy: z.string(),
  joinedAt: z.number(),
})
export type MemberDoc = z.infer<typeof MemberDoc>

export const StaffDoc = z.object({
  name: z.string().min(1),
  title: z.string(),
  email: z.string().email().nullable().default(null),
  photoPath: z.string().nullable().default(null),
  bio: z.string().default(''),
  order: z.number().int(),
  active: z.boolean().default(true),
})
export type StaffDoc = z.infer<typeof StaffDoc>

export const InviteDoc = z.object({
  email: z.string().email(),
  name: z.string().default(''),
  title: z.string().default(''),
  /** A personal line from the inviter, included in the invitation email. */
  note: z.string().default(''),
  clientId: Slug,
  role: MemberRole,
  invitedBy: z.string(),
  createdAt: z.number(),
  expiresAt: z.number(),
  status: z.enum(['pending', 'accepted', 'revoked', 'expired']),
  acceptedBy: z.string().nullable().default(null),
  acceptedAt: z.number().nullable().default(null),
})
export type InviteDoc = z.infer<typeof InviteDoc>

export const AgencySettings = z.object({
  metaBusinessId: z.string().default(''),
  notifyEmails: z.array(z.string().email()).default([]),
  fromEmail: z.string().default('Sonder Media House <portal@sondermediahouse.com>'),
  replyTo: z.string().default('hello@sondermediahouse.com'),
})
export type AgencySettings = z.infer<typeof AgencySettings>

/* ---------------------------------------------------------------- */
/* Templates: default PortalConfig for a new client                  */
/* ---------------------------------------------------------------- */

export function defaultPlatforms(): PortalConfig['platforms'] {
  return Object.fromEntries(
    PLATFORM_KEYS.map((k) => [k, { include: PLATFORMS[k].defaultIncluded, managed: PLATFORMS[k].defaultManaged }]),
  ) as PortalConfig['platforms']
}

export const TEMPLATES: Record<string, { label: string; description: string; config: () => PortalConfig }> = {
  'restaurant-default': {
    label: 'Restaurant / bar',
    description: 'Every section, Meta + TikTok managed, listings for reference.',
    config: () => ({
      sections: SECTION_IDS.map((id) => ({ id, enabled: true })),
      platforms: defaultPlatforms(),
      platformOverrides: {},
      team: [],
      modules: { galleries: false },
      scopeNotes: '',
      copy: {},
      welcomeMedia: null,
      templateId: 'restaurant-default',
    }),
  },
  'hotel-default': {
    label: 'Hotel / resort',
    description: 'No menus or specials; audience and assets emphasised.',
    config: () => ({
      sections: SECTION_IDS.map((id) => ({ id, enabled: id !== 'links' && id !== 'specials' })),
      platforms: defaultPlatforms(),
      platformOverrides: {},
      team: [],
      modules: { galleries: false },
      scopeNotes: '',
      copy: {},
      welcomeMedia: null,
      templateId: 'hotel-default',
    }),
  },
}

export function resolvePlatform(config: PortalConfig, brandId: string, platform: keyof PortalConfig['platforms']): PlatformSetting {
  const base = config.platforms[platform] ?? { include: false, managed: false }
  const override = config.platformOverrides?.[brandId]?.[platform]
  return { ...base, ...override }
}
